// src/pages/SetPasswordPage.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import { markFirstLoginComplete } from '../api';
import { useAuth } from '../context/AuthContext';

export default function SetPasswordPage() {
  const navigate               = useNavigate();
  const { member, setFirstLogin } = useAuth();

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      // 1. Update password in Firebase
      await updatePassword(auth.currentUser, password);

      // 2. Mark first login complete in the Sheet
      await markFirstLoginComplete();

      // 3. Update local state so ProtectedRoute lets them through
      setFirstLogin(false);

      // 4. Go to dashboard
      navigate('/dashboard');

    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setError('For security, please log out and log in again before setting a new password.');
      } else {
        setError('Failed to set password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const NAVY = '#1B3A6B';
  const GOLD = '#C9A84C';

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '40px 36px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: NAVY, margin: '0 auto 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `3px solid ${GOLD}`
          }}>
            <span style={{ fontSize: '28px' }}>🔒</span>
          </div>
          <h2 style={{ color: NAVY, margin: '0 0 8px', fontSize: '22px' }}>
            Welcome{member?.name ? `, ${member.name.split(' ')[0]}` : ''}!
          </h2>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
            Please set a new password to continue.
            <br />Your temporary password will no longer work after this.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: '#c0392b', fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          display: 'flex', flexDirection: 'column', gap: '16px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#444' }}>
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              style={{
                padding: '11px 14px', border: '2px solid #e0e0e0',
                borderRadius: '8px', fontSize: '14px',
                outline: 'none', boxSizing: 'border-box', width: '100%'
              }}
              disabled={loading}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#444' }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat your new password"
              style={{
                padding: '11px 14px', border: '2px solid #e0e0e0',
                borderRadius: '8px', fontSize: '14px',
                outline: 'none', boxSizing: 'border-box', width: '100%'
              }}
              disabled={loading}
            />
          </div>

          {/* Password strength hint */}
          {password.length > 0 && (
            <div style={{
              fontSize: '12px',
              color: password.length >= 8 ? '#1a6b3a' : '#c0392b'
            }}>
              {password.length >= 8
                ? '✓ Password length is good'
                : `✗ ${8 - password.length} more characters needed`}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '13px',
              background: NAVY, color: '#fff',
              border: 'none', borderRadius: '8px',
              fontSize: '15px', fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginTop: '4px'
            }}
          >
            {loading ? 'Setting password...' : 'Set My Password'}
          </button>
        </form>

        <p style={{
          textAlign: 'center', fontSize: '12px',
          color: '#999', marginTop: '20px', marginBottom: 0
        }}>
          Achimota School 1992 Year Group Alumni Portal
        </p>
      </div>
    </div>
  );
}